import { createContext, useContext } from "react";

import type { components } from "@/api/schema";

export type Me = components["schemas"]["MeOut"];
export type Permission = components["schemas"]["Permission"] | string;

/** `/me`'s cache key. A 403 anywhere invalidates it (main.tsx), so a revoked role shows at once. */
export const ME_KEY = ["me"] as const;

const LEGACY_MAP: Record<string, string[]> = {
  view: [
    "read:questions",
    "read:jds",
    "read:taxonomy",
    "page:review",
    "page:questions",
    "page:taxonomy",
    "view",
  ],
  review: ["edit:review_decision", "review"],
  curate: ["edit:curate_cluster", "delete:question_soft", "curate"],
  ingest: ["create:ingest_upload", "create:ingest_generate", "create:ingest", "ingest"],
  run_pipeline: ["execute:pipeline_run", "run_pipeline"],
  probe: ["execute:probe_run", "probe"],
  manage_taxonomy: [
    "create:taxonomy",
    "edit:taxonomy",
    "delete:taxonomy",
    "manage_taxonomy",
  ],
  run_maintenance: ["execute:maintenance", "run_maintenance"],
  manage_picker: ["edit:picker", "manage_picker"],
  manage_access: [
    "access:roles_edit",
    "access:roles_create",
    "access:roles_delete",
    "access:members_assign",
    "manage_access",
  ],
  purge_questions: ["delete:question_purge", "purge_questions"],
  manage_jd: [
    "create:jds",
    "create:jd_gap_draft",
    "edit:jd_skills",
    "edit:jd_aliases",
    "edit:jd_status",
    "execute:jd_rematch",
    "manage_jd",
  ],
  purge_jd: ["delete:jds_purge", "purge_jd"],
};

/**
 * The signed-in member, provided by `AuthGate` once `/me` has answered with a role. Nothing
 * inside the app renders before that, so `useMember` never has to handle "not yet".
 */
export const MemberContext = createContext<Me | null>(null);

export function useMember(): Me {
  const member = useContext(MemberContext);
  if (!member) throw new Error("useMember() outside <AuthGate>");
  return member;
}

/**
 * Whether to offer a control. Pure function taking member.
 * Strictly checks granular permissions (e.g. "page:review", "export:questions_docx")
 * against the member's assigned permissions.
 * Legacy broad verbs (e.g. "review", "curate") are mapped to their required granular scopes.
 */
export function checkCan(member: Me | null | undefined, permission: Permission | string): boolean {
  if (!member) return false;
  const perms = member.permissions;
  if (perms.includes(permission)) return true;
  if (perms.includes("admin")) return true;

  // Having page access implies default view / read permission:
  if (permission === "read:taxonomy" && perms.includes("page:taxonomy")) return true;
  if (permission === "read:jds" && perms.includes("page:jds")) return true;
  if (permission === "read:questions" && (perms.includes("page:questions") || perms.includes("page:review"))) return true;
  if (permission === "read:picker" && perms.includes("page:picker")) return true;

  // If user holds the legacy broad "manage_taxonomy" permission, grant all taxonomy capabilities:
  if (
    perms.includes("manage_taxonomy") &&
    (permission === "page:taxonomy" ||
      permission === "read:taxonomy" ||
      permission === "create:taxonomy" ||
      permission === "edit:taxonomy" ||
      permission === "delete:taxonomy")
  ) {
    return true;
  }

  // If checking a legacy broad permission (e.g. "review", "curate"), verify if user holds any of its granular scopes
  const mapped = LEGACY_MAP[permission];
  if (mapped && mapped.some((k) => perms.includes(k))) return true;

  return false;
}

/**
 * Whether to offer a control. Hook variant of checkCan.
 */
export function useCan(permission: Permission | string): boolean {
  return checkCan(useMember(), permission);
}


/**
 * What queueing (or cancelling) each job kind takes — a copy of core/access.py JOB_PERMISSIONS,
 * for hiding buttons only. A kind missing here is treated as admin-only, as it is there.
 */
const JOB_PERMISSIONS: Record<string, Permission> = {
  pipeline: "execute:pipeline_run",
  probe: "execute:probe_run",
  generate: "create:ingest",
  jd_analyze: "create:jds",
  jd_rematch: "execute:jd_rematch",
};

export function jobPermission(kind: string): Permission {
  return JOB_PERMISSIONS[kind] ?? "execute:maintenance";
}
